import { graphqlRequest } from "@/graphql/client";
import * as graphQlMutations from "@/graphql/mutations";
import * as graphQlQueries from "@/graphql/queries";
import { normalizeTimestamp } from "@/utils/unifiedEmail";
import { extractBodyString } from "../propertyCore/propertyApi";

function safeParseJson(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeNextPageCursor(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      // Opaque cursor, round-trip as-is.
    }
    return trimmed;
  }
  if (typeof value === "object") return value;
  return null;
}

function extractPagination(parsedBody) {
  const directNextPage = parsedBody?.next_page ?? parsedBody?.LastEvaluatedKey ?? null;
  const nestedNextPage = parsedBody?.pagination?.next_page ?? null;
  const nextPage = normalizeNextPageCursor(directNextPage || nestedNextPage || null);
  return {
    has_next: Boolean(nextPage),
    next_page: nextPage,
  };
}

function asAwsJsonCursor(nextPage) {
  if (nextPage === null || nextPage === undefined || nextPage === "") return null;
  if (typeof nextPage === "string") return nextPage;
  return JSON.stringify(nextPage);
}

function getDocument(bucket, name, kind) {
  const doc = bucket?.[name];
  if (typeof doc !== "string" || doc.length === 0) {
    throw new Error(`Missing generated ${name} ${kind}. Run \`amplify push\` and regenerate.`);
  }
  return doc;
}

function parseApiBody(rawBody) {
  if (typeof rawBody === "string") {
    const bodyString = extractBodyString(rawBody);
    return safeParseJson(bodyString);
  }
  if (rawBody && typeof rawBody === "object") return rawBody;
  return {};
}

function readResponse(apiResult) {
  const statusCode = apiResult?.statusCode ?? null;
  const body = parseApiBody(apiResult?.body);
  return {
    ok: Number(statusCode) >= 200 && Number(statusCode) < 300,
    statusCode,
    body,
    code: body?.code ? String(body.code) : "",
    latestOfferId: body?.latest_offer_id ? String(body.latest_offer_id) : "",
  };
}

function normalizeFeedItem(row, index) {
  const raw = row && typeof row === "object" ? row : {};
  const rowType = String(raw.type ?? "").trim().toUpperCase();
  const metadata = safeParseJson(raw.metadata);
  const normalizedMetadata =
    metadata && typeof metadata === "object" && !Array.isArray(metadata) ? metadata : {};
  const insertDate =
    normalizeTimestamp(raw.insert_date || raw.created_at || raw.last_modified || null) ??
    raw.insert_date ??
    raw.created_at ??
    raw.last_modified ??
    "";
  const statusFromOfferEvent =
    rowType === "OFFER" ? String(raw.offer_event ?? "").trim().toUpperCase() : "";
  const statusFromEvent =
    statusFromOfferEvent === "ACCEPT"
      ? "ACCEPTED"
      : statusFromOfferEvent === "WITHDRAW"
        ? "WITHDRAWN"
        : statusFromOfferEvent;
  const status =
    String(raw.offer_status ?? raw.status ?? statusFromEvent ?? "").trim().toUpperCase() || "MESSAGE";
  const metadataMessage =
    typeof normalizedMetadata.message === "string" ? normalizedMetadata.message.trim() : "";
  const isMessageRow = rowType === "MESSAGE" || status === "MESSAGE";
  const messageText =
    typeof raw.message === "string" && raw.message.trim()
      ? raw.message
      : typeof raw.content === "string" && raw.content.trim()
        ? raw.content
        : isMessageRow
          ? metadataMessage
          : "";
  const senderRole = String(raw.role ?? raw.sender_role ?? "").trim();

  return {
    id: String(raw.id ?? raw.message_id ?? raw.offer_id ?? `feed-${Date.now()}-${index}`).trim(),
    type: rowType || "MESSAGE",
    message: messageText,
    sender: raw.sender ?? "",
    receiver: raw.receiver ?? "",
    sender_role: senderRole,
    receiver_role: raw.receiver_role ?? "",
    role: senderRole,
    collection_id:
      typeof raw.collection_id === "string" && raw.collection_id.trim()
        ? raw.collection_id.trim()
        : typeof raw.thread === "string" && raw.thread.trim()
          ? raw.thread.trim()
          : null,
    insert_date: insertDate,
    status,
    offer_id: raw.offer_id ?? null,
    offer_event: raw.offer_event ?? null,
    offer_status: raw.offer_status ?? null,
    offer_amount: raw.offer_amount ?? normalizedMetadata.offer_amount ?? null,
    offer_terms: safeParseJson(raw.offer_terms),
    deallens_id: raw.deallens_id ?? null,
    supersedes_offer_id: raw.supersedes_offer_id ?? null,
    direction: raw.direction ?? null,
    event_id: raw.event_id ?? null,
    campaign: raw.campaign ?? null,
    metadata: {
      ...normalizedMetadata,
      offer_status: status,
      ...(raw.offer_amount != null ? { offer_amount: raw.offer_amount } : {}),
    },
    raw,
  };
}

async function runCollectionQuery(queryName, fieldName, variables) {
  const query = getDocument(graphQlQueries, queryName, "query");
  const response = await graphqlRequest({
    query,
    variables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });
  const apiResult = response?.data?.[fieldName];
  const parsed = readResponse(apiResult);
  if (!parsed.ok) {
    const message =
      typeof parsed.body === "string"
        ? parsed.body
        : parsed.body?.message || parsed.body?.error || `Failed ${fieldName} request.`;
    throw new Error(String(message));
  }
  const parsedBody = parsed.body && typeof parsed.body === "object" ? parsed.body : {};
  const rows = Array.isArray(parsedBody?.items) ? parsedBody.items : [];
  return {
    data: rows.map((row, index) => normalizeFeedItem(row, index)),
    items: rows,
    pagination: extractPagination(parsedBody),
    statusCode: parsed.statusCode,
    isEmpty: rows.length === 0,
    body: parsedBody,
  };
}

export async function fetchCollectionFeed({
  customer,
  propertyId,
  collectionId,
  role = null,
  nextPage = null,
  limit = null,
  order = null,
}) {
  const variables = {
    customer: String(customer ?? "").trim(),
    property_id: String(propertyId ?? "").trim(),
    collection_id: String(collectionId ?? "").trim(),
    ...(String(role ?? "").trim() ? { role: String(role).trim() } : {}),
    ...(limit != null ? { limit } : {}),
    ...(String(order ?? "").trim() ? { order: String(order).trim().toLowerCase() } : {}),
    next_page: asAwsJsonCursor(nextPage),
  };
  return runCollectionQuery("getCollectionFeed", "getCollectionFeed", variables);
}

export async function fetchCollectionOffers({
  customer,
  propertyId,
  collectionId,
  role = null,
  nextPage = null,
  limit = null,
  order = null,
}) {
  const variables = {
    customer: String(customer ?? "").trim(),
    property_id: String(propertyId ?? "").trim(),
    collection_id: String(collectionId ?? "").trim(),
    ...(String(role ?? "").trim() ? { role: String(role).trim() } : {}),
    ...(limit != null ? { limit } : {}),
    ...(String(order ?? "").trim() ? { order: String(order).trim().toLowerCase() } : {}),
    next_page: asAwsJsonCursor(nextPage),
  };
  return runCollectionQuery("getCollectionOffers", "getCollectionOffers", variables);
}

export async function fetchCollectionTips({
  customer,
  propertyId,
  role = null,
  investor = null,
  nextPage = null,
  maxItems = null,
}) {
  const variables = {
    customer: String(customer ?? "").trim(),
    property_id: String(propertyId ?? "").trim(),
    ...(String(role ?? "").trim() ? { role: String(role).trim() } : {}),
    ...(String(investor ?? "").trim() ? { investor: String(investor).trim() } : {}),
    ...(maxItems != null ? { max_items: maxItems } : {}),
    next_page: asAwsJsonCursor(nextPage),
  };
  return runCollectionQuery("getCollectionTips", "getCollectionTips", variables);
}

export async function makeOffer(input) {
  const mutation = getDocument(graphQlMutations, "makeOffer", "mutation");
  const response = await graphqlRequest({
    query: mutation,
    variables: input,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });
  return readResponse(response?.data?.makeOffer);
}

export async function sendMessage(input) {
  const mutation = getDocument(graphQlMutations, "sendMessage", "mutation");
  const response = await graphqlRequest({
    query: mutation,
    variables: input,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });
  return readResponse(response?.data?.sendMessage);
}

export { normalizeFeedItem, readResponse };
